/**
 * Validation utility functions
 */

export const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const validateURL = (url) => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

export const validateLinkedInURL = (url) => {
  if (!url) return true; // Optional field
  return url.includes('linkedin.com') && validateURL(url);
};

export const validateRequiredField = (value) => {
  return value && value.toString().trim().length > 0;
};

export const validateMaxLength = (value, maxLength) => {
  if (!value) return true; // Optional field
  return value.toString().length <= maxLength;
};

export const validateMinLength = (value, minLength) => {
  if (!value) return false;
  return value.toString().length >= minLength;
};

export const validateInterview = (interview) => {
  const errors = {};
  
  if (!validateRequiredField(interview.first_name)) {
    errors.first_name = 'First name is required';
  }
  
  if (!validateRequiredField(interview.last_name)) {
    errors.last_name = 'Last name is required';
  }
  
  if (!validateRequiredField(interview.company)) {
    errors.company = 'Company is required';
  }
  
  if (!validateRequiredField(interview.email)) {
    errors.email = 'Email is required';
  } else if (!validateEmail(interview.email)) {
    errors.email = 'Invalid email format';
  }
  
  if (interview.linkedin_url && !validateLinkedInURL(interview.linkedin_url)) {
    errors.linkedin_url = 'Invalid LinkedIn URL';
  }
  
  if (interview.transcript_url && !validateURL(interview.transcript_url)) {
    errors.transcript_url = 'Invalid transcript URL';
  }
  
  if (!validateMaxLength(interview.first_name, 100)) {
    errors.first_name = 'First name must be less than 100 characters';
  }
  
  if (!validateMaxLength(interview.last_name, 100)) {
    errors.last_name = 'Last name must be less than 100 characters';
  }
  
  if (!validateMaxLength(interview.company, 200)) {
    errors.company = 'Company name must be less than 200 characters';
  }
  
  if (!validateMaxLength(interview.job_title, 200)) {
    errors.job_title = 'Job title must be less than 200 characters';
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

export const validateCSVFile = (file) => {
  const errors = [];
  
  if (!file) {
    errors.push('File is required');
    return { isValid: false, errors };
  }
  
  if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
    errors.push('File must be a CSV file');
  }
  
  if (file.size > 50 * 1024 * 1024) { // 50MB limit
    errors.push('File size must be less than 50MB');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

export const validateBatchProcessingRequest = (request) => {
  const errors = {};
  
  if (!request.interview_ids || !Array.isArray(request.interview_ids) || request.interview_ids.length === 0) {
    errors.interview_ids = 'At least one interview must be selected';
  }
  
  if (!request.tasks || !Array.isArray(request.tasks) || request.tasks.length === 0) {
    errors.tasks = 'At least one task must be selected';
  }
  
  if (request.batch_name && !validateMaxLength(request.batch_name, 200)) {
    errors.batch_name = 'Batch name must be less than 200 characters';
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

export const sanitizeInput = (input) => {
  if (typeof input !== 'string') return input;
  
  return input
    .trim()
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '') // Remove script tags
    .replace(/javascript:/gi, '') // Remove javascript: protocol
    .replace(/on\w+=/gi, ''); // Remove event handlers
};

export const validateSearchTerm = (term) => {
  if (!term || typeof term !== 'string') return false;
  
  // Must be at least 2 characters
  if (term.trim().length < 2) return false;
  
  // Must not contain only special characters
  if (!/[a-zA-Z0-9]/.test(term)) return false;
  
  return true;
};

export const validatePagination = (page, pageSize, maxPageSize = 100) => {
  const errors = {};
  
  if (!Number.isInteger(page) || page < 1) {
    errors.page = 'Page must be a positive integer';
  }
  
  if (!Number.isInteger(pageSize) || pageSize < 1 || pageSize > maxPageSize) {
    errors.pageSize = `Page size must be between 1 and ${maxPageSize}`;
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};